import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { apiClient } from '@/api'

type RegionList = Awaited<ReturnType<typeof apiClient.metadata.regions.list>>
export type AdministrativeRegion = RegionList['items'][number]

type SaveState = 'idle' | 'saving' | 'saved' | 'recovering' | 'reconciled' | 'unresolved'

export const useAdministrativeRegionsStore = defineStore('administrativeRegions', () => {
  const snapshot = ref<RegionList | null>(null)
  const loading = ref(false)
  const pendingCode = ref<string | null>(null)
  const loadFailed = ref(false)
  const authoritative = ref(false)
  const saveState = ref<SaveState>('idle')
  let generation = 0

  const items = computed(() => snapshot.value?.items ?? [])
  const canMutate = computed(() => authoritative.value && !loading.value && pendingCode.value === null)

  async function load() {
    if (pendingCode.value !== null) {
      return
    }
    const request = ++generation
    loading.value = true
    loadFailed.value = false
    try {
      const result = await apiClient.metadata.regions.list()
      if (request !== generation) {
        return
      }
      snapshot.value = result
      authoritative.value = true
      if (saveState.value === 'unresolved') {
        saveState.value = 'reconciled'
      }
    }
    catch {
      if (request === generation) {
        loadFailed.value = true
        authoritative.value = false
      }
    }
    finally {
      if (request === generation) {
        loading.value = false
      }
    }
  }

  async function toggle(code: AdministrativeRegion['code']) {
    const region = items.value.find(item => item.code === code)
    if (!region || !canMutate.value) {
      return
    }
    const request = ++generation
    pendingCode.value = code
    loading.value = false
    loadFailed.value = false
    saveState.value = 'saving'
    try {
      const result = await apiClient.metadata.regions.setEnabled({ code, enabled: region.status !== 1 })
      if (request !== generation) {
        return
      }
      snapshot.value = result
      authoritative.value = true
      saveState.value = 'saved'
    }
    catch {
      if (request !== generation) {
        return
      }
      // A failed response may follow a committed write. Re-read before allowing another command.
      authoritative.value = false
      saveState.value = 'recovering'
      try {
        const result = await apiClient.metadata.regions.list()
        if (request !== generation) {
          return
        }
        snapshot.value = result
        authoritative.value = true
        saveState.value = 'reconciled'
      }
      catch {
        if (request === generation) {
          loadFailed.value = true
          saveState.value = 'unresolved'
        }
      }
    }
    finally {
      if (request === generation) {
        pendingCode.value = null
      }
    }
  }

  function reset() {
    generation++
    snapshot.value = null
    loading.value = false
    pendingCode.value = null
    loadFailed.value = false
    authoritative.value = false
    saveState.value = 'idle'
  }

  return {
    items,
    canMutate,
    hasLoaded: computed(() => snapshot.value !== null),
    loading: computed(() => loading.value),
    pendingCode: computed(() => pendingCode.value),
    loadFailed: computed(() => loadFailed.value),
    saveState: computed(() => saveState.value),
    load,
    toggle,
    reset,
  }
})
